// src/App.js

import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import Layout from "./components/Layout";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorBoundary from "./components/ErrorBoundary";

import HomePage from "./pages/HomePage";
import ItemsPage from "./pages/ItemsPage";
import ItemPage from "./pages/ItemPage";
import CartoonsPage from "./pages/CartoonPage";

/* Admin and Login are code-split. They're only ever used by the artist, but
   under the old setup every gallery visitor downloaded them as part of the
   main bundle — on gallery wifi, on the page a QR code lands on. React.lazy
   moves them into chunks fetched only when those routes are visited. */
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));

/**
 * Reads the admin token's expiry without verifying it.
 *
 * auth.py issues `<payload>.<sig>` where payload is base64url of
 * "exp:<unix-timestamp>". The signature is verified server-side — this only
 * reads the claim, which is enough to avoid routing someone into an admin page
 * that will immediately 401 and bounce them back to login.
 *
 * Returns false on any malformed token rather than throwing.
 */
function hasValidAdminToken() {
  const token = localStorage.getItem("adminToken");
  if (!token) return false;

  try {
    const payload = token.split(".")[0];
    const pad = "=".repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/") + pad);
    const match = decoded.match(/^exp:(\d+)$/);
    if (!match) return false;
    return Number(match[1]) * 1000 > Date.now();
  } catch {
    return false;
  }
}

export default function App() {
  const location = useLocation();

  /* Derived from the token rather than the separate "isAdmin" flag.
     Those two could desync: the token expires after 24 hours while isAdmin
     stayed "true" forever, so a returning visitor was routed into AdminPage,
     hit a 401, and got bounced back to login — a round trip and a confusing
     flash for something knowable up front.

     The old effect also listed `isAdmin` in its own dependency array while
     calling setIsAdmin inside. The !== guard stopped it looping, but a
     self-referential effect is a trap waiting for the next edit. */
  const [isAdmin, setIsAdmin] = useState(hasValidAdminToken);

  const refreshAuth = useCallback(() => {
    setIsAdmin(hasValidAdminToken());
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [location.pathname, refreshAuth]);

  /* Keeps tabs in sync: logging out in one tab shouldn't leave another
     showing an admin panel it can no longer talk to. */
  useEffect(() => {
    window.addEventListener("storage", refreshAuth);
    return () => window.removeEventListener("storage", refreshAuth);
  }, [refreshAuth]);

  return (
    <Layout>
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/cartoons" element={<CartoonsPage />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/auction/item/:itemId" element={<ItemPage />} />

            <Route
              path="/login"
              element={
                isAdmin ? (
                  <Navigate to="/admin" replace />
                ) : (
                  <LoginPage setIsAdmin={setIsAdmin} />
                )
              }
            />

            {/* The real guard is server-side: every admin endpoint requires
                the HMAC token via require_admin. This only decides what to
                render, so a spoofed localStorage value gets an admin shell
                with no data rather than any actual access. */}
            <Route
              path="/admin"
              element={isAdmin ? <AdminPage /> : <Navigate to="/login" replace />}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
}